/**
 * The console's one store — SPEC_5 §4.3: today, the portfolio, the skills, the run's events, the
 * heartbeat and the stream.
 *
 * **What re-reads what is written here and nowhere else**:
 *
 *   on load          today, portfolio and skills; then the run's events once today names a run
 *   a `datapacks`    today and the portfolio
 *   a `runs`         today (and the new run's events, when today names a different run)
 *   `resync`         everything
 *   `heartbeat`      today, only when the worker's `alive` flips: the worker-dead banner is the
 *                    server's state with the server's sentence, so the console asks for it
 *
 * **No polling.** Every re-read is an event's, and a panel never re-reads on its own behalf.
 *
 * *Rejected: patching today from the `run` event's document*, which would mean the browser deciding
 * which of SPEC_4's states a run row is in. Two places deciding "is it ready" will one day disagree
 * on the morning that matters (SPEC_5 Decision 2).
 */

import { create } from "zustand";
import { Failed, browserSays, read } from "../api/client";
import type { Heartbeat, Portfolio, RunEvent, RunEvents, Skills, Today } from "../api/types/competition";
import { useSession } from "./session";
import { bufferFor } from "../stream/offsets";

export interface Slot<T> {
  data: T | null;
  /** `X-Stream-Head` from the answer that produced `data`. The stream opens at the LOWEST head the
   *  tab holds: a read that answered earlier would otherwise miss everything after it. */
  head: string | null;
  loading: boolean;
  /** Two attempts, then stop. Never a silent third. */
  attempts: 0 | 1 | 2;
  /** The sentence to render instead of the data: the browser's for no answer, the server's own
   *  for a 5xx that answered or a refusal. */
  says: string | null;
}

const blank = <T,>(): Slot<T> => ({ data: null, head: null, loading: false, attempts: 0, says: null });

export type StreamStatus = "connecting" | "live" | "reconnecting" | "unreachable";

export interface StreamState {
  status: StreamStatus;
  lastEventId: string | null;
  /** When the connection dropped, by the last heartbeat's clock. */
  droppedAt: string | null;
  /** The last heartbeat this tab actually received, by the viewer's clock — only ever compared
   *  with itself, to tell a silent server from a dead worker. */
  lastHeartbeatAt: number | null;
}

/** The run the viewer shows, built from `GET /runs/{id}/events` and then the stream's tail. */
export interface RunLog {
  id: string | null;
  head: string | null;
  events: RunEvent[];
  preamble: string;
  /** Written AT their offsets, never appended — stream/offsets.ts. */
  thinking: string;
  text: string;
  loading: boolean;
}

type SlotKey = "today" | "portfolio" | "skills";

const PATHS: Record<SlotKey, string> = { today: "/today", portfolio: "/portfolio", skills: "/skills" };
const NOUNS: Record<SlotKey, string> = { today: "today", portfolio: "the portfolio", skills: "the skills" };

interface CompetitionState {
  today: Slot<Today>;
  portfolio: Slot<Portfolio>;
  skills: Slot<Skills>;
  run: RunLog;
  heartbeat: Heartbeat | null;
  stream: StreamState;

  /** Today, the portfolio and the skills, then the run. On load and on `resync`. */
  loadAll: () => Promise<void>;
  load: (key: SlotKey, attempt?: 1 | 2) => Promise<void>;
  /** The reader's second attempt, and there is no third. */
  retry: (key: SlotKey) => void;
  heartbeatArrived: (beat: Heartbeat) => void;
  /** One run event off the stream. Ignored unless it belongs to the run on screen. */
  runEvent: (event: RunEvent & { session_id: string | null }) => void;
  setStream: (next: Partial<StreamState>) => void;
  lowestHead: () => string | null;
}

/** Heads are `<boot>-<n>`. */
function splitHead(head: string): [string, number] {
  const cut = head.lastIndexOf("-");
  return cut < 0 ? [head, 0] : [head.slice(0, cut), Number(head.slice(cut + 1))];
}

/** The lowest by `n` within one boot. Across two boots there is nothing to compare, so the server
 *  gets one of them and answers `resync`, which is the right answer. */
function lowerHead(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  const [aBoot, aN] = splitHead(a);
  const [bBoot, bN] = splitHead(b);
  if (aBoot !== bBoot) return a;
  return aN <= bN ? a : b;
}

const emptyRun = (id: string | null): RunLog => ({
  id,
  head: null,
  events: [],
  preamble: "",
  thinking: "",
  text: "",
  loading: false,
});

export const useCompetition = create<CompetitionState>((set, get) => {
  /** The read in flight per payload. A caller that asks while one is out gets that read, and the
   *  payload is read once more after it: the change that prompted the ask may have landed after the
   *  in-flight read's query ran, and the app root waits on it before opening the stream. */
  const inFlight = new Map<SlotKey, Promise<void>>();
  const again = new Set<SlotKey>();

  const put = <K extends SlotKey>(key: K, next: Partial<CompetitionState[K]>) =>
    set((state) => ({ [key]: { ...state[key], ...next } }) as Partial<CompetitionState>);

  /** The run today names, read once per run id. A new run id starts a clean log. */
  const followRun = async () => {
    const id = get().today.data?.run?.id ?? null;
    if (id === get().run.id) return;
    set({ run: { ...emptyRun(id), loading: id !== null } });
    if (!id) return;
    try {
      const { data, head } = await read<RunEvents>(`/runs/${id}/events`);
      const was = get().run;
      if (was.id !== id) return; // a newer run took its place while this was in flight
      // Not `seedFromEvents`, which clears first: a delta the stream wrote while this read was out
      // must stay, and history and tail both land at their offsets, so neither can double the other.
      const buffer = bufferFor(id);
      for (const e of data.events) {
        if (e.type !== "thinking_delta" && e.type !== "text_delta") continue;
        buffer.write(e.type, typeof e.data.offset === "number" ? e.data.offset : 0, typeof e.data.text === "string" ? e.data.text : "");
      }
      // Whatever the stream delivered while this read was out is kept beside the history, by `seq`.
      const seen = new Set(data.events.map((e) => e.seq));
      const events = [
        ...data.events.filter((e) => e.type !== "thinking_delta" && e.type !== "text_delta"),
        ...was.events.filter((e) => !seen.has(e.seq)),
      ].sort((a, b) => a.seq - b.seq);
      const preamble = events.find((e) => e.type === "preamble");
      set({
        run: {
          id,
          head,
          events,
          preamble: typeof preamble?.data.text === "string" ? preamble.data.text : "",
          thinking: buffer.read("thinking_delta"),
          text: buffer.read("text_delta"),
          loading: false,
        },
      });
    } catch (error) {
      if (error instanceof Failed && error.trouble.kind === "signed out") {
        useSession.getState().bounced(error.trouble.refusal.state, error.trouble.refusal.says);
      }
      if (get().run.id === id) set({ run: { ...get().run, loading: false } });
    }
  };

  const readOnce = async (key: SlotKey, attempt: 1 | 2) => {
    put(key, { loading: true, attempts: attempt });
    try {
      const { data, head } = await read<never>(PATHS[key]);
      put(key, { data, head, loading: false, attempts: 0, says: null });
      if (key === "today") void followRun();
    } catch (error) {
      if (!(error instanceof Failed)) throw error;
      const t = error.trouble;
      if (t.kind === "signed out") {
        put(key, { loading: false });
        useSession.getState().bounced(t.refusal.state, t.refusal.says);
      } else if (t.kind === "unreachable") {
        // One state on a GET, because re-reading is free. The first failure names the panel in the
        // browser's sentence; the second says so, and there is no third.
        const name = attempt === 1 ? "unreachable" : "unreachable again";
        put(key, { loading: false, says: browserSays(name, { noun: NOUNS[key] }) });
      } else if (t.kind === "broke") {
        put(key, { loading: false, says: t.says });
      } else {
        put(key, { loading: false, data: null, attempts: 0, says: t.refusal.says });
      }
    }
  };

  return {
    today: blank<Today>(),
    portfolio: blank<Portfolio>(),
    skills: blank<Skills>(),
    run: emptyRun(null),
    heartbeat: null,
    stream: { status: "connecting", lastEventId: null, droppedAt: null, lastHeartbeatAt: null },

    loadAll: async () => {
      await Promise.all([get().load("today"), get().load("portfolio"), get().load("skills")]);
    },

    load: (key, attempt = 1) => {
      const running = inFlight.get(key);
      if (running) {
        again.add(key);
        return running;
      }
      const reading = (async () => {
        do {
          again.delete(key);
          await readOnce(key, attempt);
        } while (again.has(key));
      })().finally(() => inFlight.delete(key));
      inFlight.set(key, reading);
      return reading;
    },

    retry: (key) => {
      if (get()[key].attempts >= 2) return;
      void get().load(key, 2);
    },

    heartbeatArrived: (beat) => {
      const was = get().heartbeat;
      set({ heartbeat: beat, stream: { ...get().stream, lastHeartbeatAt: Date.now() } });
      if (was && was.worker.alive !== beat.worker.alive) void get().load("today");
    },

    runEvent: (event) => {
      const run = get().run;
      if (!run.id || event.session_id !== run.id) return;
      if (event.type === "thinking_delta" || event.type === "text_delta") {
        const buffer = bufferFor(run.id);
        const offset = typeof event.data.offset === "number" ? event.data.offset : 0;
        buffer.write(event.type, offset, typeof event.data.text === "string" ? event.data.text : "");
        set({ run: { ...run, thinking: buffer.read("thinking_delta"), text: buffer.read("text_delta") } });
        return;
      }
      // A replay after a reconnect resends what the GET already held: `seq` is the run's own order,
      // so a seen one is skipped rather than drawn twice.
      if (run.events.some((e) => e.seq === event.seq)) return;
      const preamble = event.type === "preamble" && typeof event.data.text === "string" ? event.data.text : run.preamble;
      const events = [...run.events, event].sort((a, b) => a.seq - b.seq);
      set({ run: { ...run, events, preamble } });
    },

    setStream: (next) => set((state) => ({ stream: { ...state.stream, ...next } })),

    lowestHead: () => {
      const state = get();
      return [state.today.head, state.portfolio.head, state.skills.head, state.run.head].reduce<
        string | null
      >(lowerHead, null);
    },
  };
});
